<template>
  <div
    :class="uiAlertClass"
    class="ui-alert"
    role="alert"
  >
    <Fa
      v-if="icon"
      class="ui-alert__icon ui-icon--large"
      :icon="uiAlertIcon"
    />

    <div class="ui-alert__content">
      <slot />
      <button
        v-if="actionText"
        class="ui-button ui-button--secondary ui-alert__action--mobile-only"
        type="button"
        data-test="ui-alert-action"
        @click="onClick"
      >
        {{ actionText }}
      </button>
    </div>

    <div
      v-if="actionText || dismissible"
      class="ui-alert__button-wrapper"
    >
      <button
        v-if="actionText"
        class="ui-button ui-button--secondary ui-alert__action--desktop-only"
        type="button"
        data-test="ui-alert-action"
        @click="onClick"
      >
        {{ actionText }}
      </button>

      <button
        v-if="dismissible"
        class="ui-button ui-button--icon-only ui-alert__dismiss-button"
        type="button"
        data-test="ui-alert-dismiss"
        @click="dismiss"
      >
        <svg
          aria-hidden="true"
          width="20"
          height="20"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 32 32"
          class="search-field__delete-icon"
        >
          <path
            fill="#686868"
            d="M16 0a16 16 0 100 32 16 16 0 000-32zm8 21l-3 3-5-5.2-5 5.1-3-2.8 5.2-5.1L8 11l2.8-3 5.1 5.2L21 8l3 2.8-5.2 5.1 5.1 5z"
          />
        </svg>

        <span class="sr-only">
          {{ t('Dismiss') }}
        </span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import Fa from 'vue-fa'
import {
  faCheckCircle,
  faExclamationCircle,
  faExclamationTriangle,
  faInfoCircle,
  faLightbulb,
  faStar,
  type IconDefinition,
} from '@fortawesome/free-solid-svg-icons'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    actionText?: string
    dismissible?: boolean
    icon?: boolean
    mode: 'beta' | 'danger' | 'info' | 'pro-tip' | 'success' | 'warning'
  }>(),
  {
    actionText: '',
    dismissible: false,
    icon: false,
  },
)

const emit = defineEmits<{
  'dismiss': []
  'on-action-click': []
}>()

function dismiss(): void {
  emit('dismiss')
}

function onClick(): void {
  emit('on-action-click')
}

const uiAlertClass = computed(() => {
  return {
    'ui-alert--beta': props.mode === 'beta',
    'ui-alert--danger': props.mode === 'danger',
    'ui-alert--info': props.mode === 'info',
    'ui-alert--success': props.mode === 'success',
    'ui-alert--warning': props.mode === 'warning',
    'ui-alert--pro-tip': props.mode === 'pro-tip',
    'ui-alert--dismissible': props.dismissible,
    'ui-alert--action': !!props.actionText,
  }
})

const uiAlertIcon = computed<IconDefinition>(() => {
  if (props.mode === 'beta') {
    return faStar
  } else if (props.mode === 'danger') {
    return faExclamationTriangle
  } else if (props.mode === 'info') {
    return faInfoCircle
  } else if (props.mode === 'success') {
    return faCheckCircle
  } else if (props.mode === 'warning') {
    return faExclamationCircle
  } else if (props.mode === 'pro-tip') {
    return faLightbulb
  } else {
    // Fallback: info
    return faInfoCircle
  }
})
</script>

<style lang="scss">
@use "sass:color";
$icon-dimension-small: 20px;
$icon-dimension: 24px;
$dismiss-button-dimension: 44px;

.ui-alert--action,
.ui-alert--dismissible {
  @include breakpoint-min-width(medium) {
    padding: $sc-spacing-8 + $sc-spacing-2 0;
  }
}

.ui-alert {
  border-radius: $sc-border-radius;
  border: 1px solid transparent;
  color: $sc-dark-blue;
  display: flex;
  line-height: $icon-dimension-small;
  position: relative;

  @include breakpoint-min-width(medium) {
    line-height: $icon-dimension;
  }

  p:first-child {
    margin-top: 0;
  }

  p:last-child {
    margin-bottom: 0;
  }

  a {
    color: $sc-dark-blue;
    cursor: pointer;
    text-decoration: underline;
  }

  a:focus,
  a:hover {
    color: $sc-link-blue;
    box-shadow: none;
  }

  a:active {
    color: $sc-label-purple-1;
    box-shadow: none;
  }
}

.ui-alert--beta {
  background-color: transparent;
  border-color: $sc-label-purple-2;
  color: $sc-black;
}

.ui-alert--danger {
  background-color: $sc-danger-10;
  border-color: $sc-danger-40;
  color: $sc-danger;
}

.ui-alert--info {
  background-color: $sc-informative-10;
  border-color: $sc-informative-40;
}

.ui-alert--pro-tip {
  background-color: $sc-light-grey;
  border-color: $sc-mid-grey;
}

.ui-alert--success {
  background-color: color.mix($sc-positive, $sc-white, 10%);
  border-color: color.mix($sc-positive, $sc-white, 40%);
}

.ui-alert--warning {
  background-color: color.mix($sc-notice, $sc-white, 10%);
  border-color: color.mix($sc-notice, $sc-white, 35%);
}

.ui-alert__icon.ui-alert__icon {
  flex-shrink: 0;
  height: $icon-dimension-small;
  line-height: $icon-dimension;
  margin-left: $sc-spacing-16;
  margin-top: $sc-spacing-12;
  width: $icon-dimension-small;

  @include breakpoint-min-width(medium) {
    height: $icon-dimension;
    margin-top: $sc-spacing-20;
    width: $icon-dimension;
  }

  .ui-alert--beta & {
    border-color: $sc-label-purple-1;
    color: $sc-label-purple-1;
  }

  .ui-alert--danger & {
    border-color: $sc-danger;
    color: $sc-danger;
  }

  .ui-alert--info & {
    border-color: $sc-informative;
    color: $sc-informative;
  }

  .ui-alert--pro-tip & {
    border-color: $sc-dark-grey;
    color: $sc-dark-grey;
  }

  .ui-alert--success & {
    border-color: $sc-positive;
    color: $sc-positive;
  }

  .ui-alert--warning & {
    border-color: $sc-notice;
    color: $sc-notice;
  }
}

.ui-alert__content {
  flex-grow: 1;
  padding: $sc-spacing-12 $sc-spacing-16 $sc-spacing-12 $sc-spacing-16;

  @include breakpoint-min-width(medium) {
    padding: $sc-spacing-20 $sc-spacing-20 $sc-spacing-20 $sc-spacing-16;
  }
}

.ui-alert__button-wrapper {
  flex-shrink: 0;
  margin: $sc-spacing-2 $sc-spacing-2 $sc-spacing-2 0;
  right: 0;
  top: 0;

  @include breakpoint-min-width(medium) {
    margin: $sc-spacing-8 + $sc-spacing-2 $sc-spacing-20 $sc-spacing-8 + $sc-spacing-2 0;
  }

  > * {
    vertical-align: middle;
  }

  > *:not(:first-child) {
    margin-left: $sc-spacing-12;

    @include breakpoint-min-width(medium) {
      margin-left: $sc-spacing-20;
    }
  }
}

.ui-alert .ui-alert__action--desktop-only {
  display: none;

  @include breakpoint-min-width(medium) {
    display: initial;
  }
}

.ui-alert .ui-alert__action--mobile-only {
  display: none;
  margin-top: $sc-spacing-12;

  @include breakpoint-max-width(medium) {
    display: block;
  }
}

.ui-alert__dismiss-button {
  background-color: transparent;
  border-radius: $sc-border-radius;
  border: 1px solid transparent;
  cursor: pointer;
  padding-left: 0;
  padding-right: 0;
  text-align: center;
  width: $dismiss-button-dimension;

  &:hover,
  &:focus {
    box-shadow: none;

    .ui-alert--beta & {
      border-color: $sc-label-purple-1;
    }

    .ui-alert--danger & {
      border-color: $sc-danger;
    }

    .ui-alert--info & {
      border-color: $sc-informative;
    }

    .ui-alert--pro-tip & {
      border-color: $sc-dark-grey;
    }

    .ui-alert--success & {
      border-color: $sc-positive;
    }

    .ui-alert--warning & {
      border-color: $sc-notice;
    }
  }

  &:active {
    .ui-alert--beta & {
      background-color: color.mix($sc-label-purple-3, $sc-white, 20%);
    }

    .ui-alert--danger & {
      background-color: $sc-danger-20;
    }

    .ui-alert--info & {
      background-color: $sc-informative-20;
    }

    .ui-alert--pro-tip & {
      background-color: color.mix($sc-dark-grey, $sc-white, 20%);
    }

    .ui-alert--success & {
      background-color: color.mix($sc-positive, $sc-white, 20%);
    }

    .ui-alert--warning & {
      background-color: color.mix($sc-notice, $sc-white, 20%);
    }
  }
}
</style>
